import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { t } from '@lingui/core/macro';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { useProject } from '../contexts/ProjectContext';
import GoogleSheetsLoader from '../loaders/GoogleSheetsLoader';
import type { LoaderOnFileUpload } from '../loaders/shared';
import NavBar from '../components/NavBar';
import type { Block } from '../classes/Block';
import type { CardGroup } from '../utils/cardGroups';
import {
  AutoFitTargetResult,
  CardTimingValues,
  CardTimelineItem,
  buildCardTimeline,
  formatFramesToClock,
  parseClockToFrames,
} from '../utils/cardTiming';
import './CardTiming.css';

interface CardTimingHydrationProps {
  onFileUpload: LoaderOnFileUpload;
  hasExistingData: boolean;
  onSheetsUrlUpdate?: (url: string | null) => void;
  initialTemplateSpreadsheetId?: string;
  onInitialTemplateConsumed?: () => void;
  loadButtonRef?: React.RefObject<HTMLButtonElement | null>;
}

interface UpdateCardTimingOptions {
  persist?: boolean;
}

export interface ApplyAutoFitResult extends AutoFitTargetResult {
  targetFrames: number;
}

interface CardTimingProps {
  videoMode: 'cards' | 'roller';
  fps: number;
  blocks: Block[];
  cardGroups: CardGroup[];
  onUpdateCardGroupTiming: (
    cardGroupId: string,
    timingPatch: Partial<CardTimingValues>,
    options?: UpdateCardTimingOptions
  ) => Promise<void>;
  onApplyAutoFitToCards: (
    targetFrames: number,
    fadeInFrames: number,
    fadeOutFrames: number,
    slugFrames: number
  ) => Promise<ApplyAutoFitResult>;
  hydration?: CardTimingHydrationProps;
}

interface TimingFieldConfig {
  field: keyof CardTimingValues;
  label: string;
}

const parseFrameInput = (value: string): number => {
  const parsed: number = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  return Math.max(0, Math.floor(parsed));
};

const formatSignedClock = (frames: number, fps: number): string => {
  const absFrames: number = Math.abs(frames);
  const sign: string = frames < 0 ? '-' : frames > 0 ? '+' : '';
  return `${sign}${formatFramesToClock(absFrames, fps)}`;
};

interface RowInputProps {
  id: string;
  label: string;
  value: string | number;
  unit: string;
  type?: 'text' | 'number';
  placeholder?: string;
  onChange: (value: string) => void;
}

const RowInput: React.FC<RowInputProps> = ({
  id,
  label,
  value,
  unit,
  type = 'number',
  placeholder,
  onChange,
}) => (
  <div className="card-row-input">
    <label htmlFor={id}>{label}</label>
    <div className="card-target-input-wrapper">
      <input
        id={id}
        type={type}
        min={type === 'number' ? 0 : undefined}
        step={type === 'number' ? 1 : undefined}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
      />
      <span className="card-target-unit">{unit}</span>
    </div>
  </div>
);

const CardTiming: React.FC<CardTimingProps> = ({
  videoMode,
  fps,
  blocks,
  cardGroups,
  onUpdateCardGroupTiming,
  onApplyAutoFitToCards,
  hydration,
}) => {
  const { selectedProject } = useProject();
  const navigate = useNavigate();
  const [hydrationState, setHydrationState] = useState<'loading' | 'ready'>(
    hydration && !hydration.hasExistingData ? 'loading' : 'ready'
  );
  const [selectedCardGroupId, setSelectedCardGroupId] = useState<string | null>(null);
  const [targetDialogOpen, setTargetDialogOpen] = useState<boolean>(false);
  const [targetTimeInput, setTargetTimeInput] = useState<string>('00:30:00');
  const [targetFadeInInput, setTargetFadeInInput] = useState<string>('0');
  const [targetFadeOutInput, setTargetFadeOutInput] = useState<string>('0');
  const [targetSlugInput, setTargetSlugInput] = useState<string>('0');
  const [targetInputError, setTargetInputError] = useState<string | null>(null);
  const [isApplyingTarget, setIsApplyingTarget] = useState<boolean>(false);
  const [autoFitResult, setAutoFitResult] = useState<ApplyAutoFitResult | null>(null);
  const persistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingPersistRef = useRef<{ cardGroupId: string; timing: CardTimingValues } | null>(null);
  const manualDraftRef = useRef<Record<string, CardTimingValues>>({});

  useEffect(() => {
    setHydrationState(hydration && !hydration.hasExistingData ? 'loading' : 'ready');
  }, [hydration, hydration?.hasExistingData, selectedProject?.id]);

  const timeline: CardTimelineItem[] = useMemo(() => buildCardTimeline(blocks, cardGroups), [blocks, cardGroups]);
  const totalFrames: number = useMemo(
    () => timeline.reduce((sum: number, item: CardTimelineItem) => sum + item.durationFrames, 0),
    [timeline]
  );

  useEffect(() => {
    if (timeline.length === 0) {
      setSelectedCardGroupId(null);
      return;
    }
    if (!selectedCardGroupId || !timeline.some((item: CardTimelineItem) => item.cardGroupId === selectedCardGroupId)) {
      setSelectedCardGroupId(timeline[0].cardGroupId);
    }
  }, [timeline, selectedCardGroupId]);

  useEffect(() => {
    const nextDraft: Record<string, CardTimingValues> = {};
    timeline.forEach((item: CardTimelineItem) => {
      nextDraft[item.cardGroupId] = {
        cardFadeInFrames: item.cardFadeInFrames,
        cardDurationFullyUpFrames: item.cardDurationFullyUpFrames,
        cardFadeOutFrames: item.cardFadeOutFrames,
        cardSlugFrames: item.cardSlugFrames,
      };
    });
    manualDraftRef.current = nextDraft;
  }, [timeline]);

  useEffect(() => {
    return () => {
      if (persistTimerRef.current) {
        clearTimeout(persistTimerRef.current);
      }
    };
  }, []);

  const selectedCard: CardTimelineItem | undefined = selectedCardGroupId
    ? timeline.find((item: CardTimelineItem) => item.cardGroupId === selectedCardGroupId)
    : undefined;

  const selectedCardTiming: CardTimingValues | null = selectedCard
    ? {
        cardFadeInFrames: selectedCard.cardFadeInFrames,
        cardDurationFullyUpFrames: selectedCard.cardDurationFullyUpFrames,
        cardFadeOutFrames: selectedCard.cardFadeOutFrames,
        cardSlugFrames: selectedCard.cardSlugFrames,
      }
    : null;
  const timingFields: TimingFieldConfig[] = [
    { field: 'cardFadeInFrames', label: String(t`Fade In`) },
    { field: 'cardDurationFullyUpFrames', label: String(t`Duration fully up`) },
    { field: 'cardFadeOutFrames', label: String(t`Fade Out`) },
    { field: 'cardSlugFrames', label: String(t`Slug`) },
  ];

  const queuePersist = (cardGroupId: string, timing: CardTimingValues): void => {
    pendingPersistRef.current = { cardGroupId, timing };
    if (persistTimerRef.current) {
      clearTimeout(persistTimerRef.current);
    }
    persistTimerRef.current = setTimeout(() => {
      const pending = pendingPersistRef.current;
      if (!pending) return;
      void onUpdateCardGroupTiming(pending.cardGroupId, pending.timing, { persist: true });
      pendingPersistRef.current = null;
      persistTimerRef.current = null;
    }, 300);
  };

  const handleManualTimingChange = (field: keyof CardTimingValues, value: string): void => {
    if (!selectedCard || !selectedCardTiming) return;
    const parsedValue: number = parseFrameInput(value);
    const currentDraft: CardTimingValues = manualDraftRef.current[selectedCard.cardGroupId] ?? selectedCardTiming;
    const fullTiming: CardTimingValues = {
      ...currentDraft,
      [field]: parsedValue,
    };
    manualDraftRef.current[selectedCard.cardGroupId] = fullTiming;
    void onUpdateCardGroupTiming(selectedCard.cardGroupId, { [field]: parsedValue }, { persist: false });
    queuePersist(selectedCard.cardGroupId, fullTiming);
  };

  const openTargetDialog = (): void => {
    const card = selectedCard;
    if (card) {
      setTargetFadeInInput(String(card.cardFadeInFrames));
      setTargetFadeOutInput(String(card.cardFadeOutFrames));
      setTargetSlugInput(String(card.cardSlugFrames));
    }
    setTargetInputError(null);
    setTargetDialogOpen(true);
  };

  const handleApplyTargetTime = async (): Promise<void> => {
    if (isApplyingTarget) return;
    const targetFrames: number | null = parseClockToFrames(targetTimeInput, fps);
    if (targetFrames === null) {
      setTargetInputError(String(t`Invalid target time. Use mm:ss:ff.`));
      return;
    }

    const fadeInFrames: number = parseFrameInput(targetFadeInInput);
    const fadeOutFrames: number = parseFrameInput(targetFadeOutInput);
    const slugFrames: number = parseFrameInput(targetSlugInput);

    try {
      setIsApplyingTarget(true);
      const result: ApplyAutoFitResult = await onApplyAutoFitToCards(
        targetFrames,
        fadeInFrames,
        fadeOutFrames,
        slugFrames
      );
      setAutoFitResult(result);
      setTargetDialogOpen(false);
      setTargetInputError(null);
    } finally {
      setIsApplyingTarget(false);
    }
  };

  if (!selectedProject) {
    return null;
  }

  if (hydrationState === 'loading' && hydration && !hydration.hasExistingData) {
    return (
      <div className="card-timing-page">
        <div style={{ display: 'none' }}>
          <GoogleSheetsLoader
            onFileUpload={hydration.onFileUpload}
            hasExistingData={hydration.hasExistingData}
            onSheetsUrlUpdate={hydration.onSheetsUrlUpdate}
            initialTemplateSpreadsheetId={hydration.initialTemplateSpreadsheetId}
            onInitialTemplateConsumed={hydration.onInitialTemplateConsumed}
            onAutoLoadStateChange={setHydrationState}
            loadButtonRef={hydration.loadButtonRef}
          />
        </div>
        <NavBar />
        <div className="card-timing-loading">
          <CircularProgress />
        </div>
      </div>
    );
  }

  const maxProjectFrames: number = Math.max(1, totalFrames);

  return (
    <div className="card-timing-page">
      {hydration ? (
        <div style={{ display: 'none' }}>
          <GoogleSheetsLoader
            onFileUpload={hydration.onFileUpload}
            hasExistingData={hydration.hasExistingData}
            onSheetsUrlUpdate={hydration.onSheetsUrlUpdate}
            initialTemplateSpreadsheetId={hydration.initialTemplateSpreadsheetId}
            onInitialTemplateConsumed={hydration.onInitialTemplateConsumed}
            onAutoLoadStateChange={setHydrationState}
            loadButtonRef={hydration.loadButtonRef}
          />
        </div>
      ) : null}
      <NavBar />
      <div className="card-timing-content">
        <button
          onClick={() => navigate('/app')}
          className="fancy-button"
          style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}
        >
          <ArrowBackIcon style={{ fontSize: '18px' }} />
          {t`Back to Editor`}
        </button>

        <div className="card-timing-header">
          <h1>{t`Card Timing`}</h1>
          <div className="card-timing-total">
            {t`Total project time`}: {formatFramesToClock(totalFrames, fps)} ({totalFrames} {t`frames`})
          </div>
        </div>

        {videoMode !== 'cards' ? (
          <Alert severity="info">{t`Card Timing is available only when mode is set to Cards.`}</Alert>
        ) : (
          <>
            {autoFitResult ? (
              <Alert severity={autoFitResult.deltaFrames === 0 ? 'success' : 'info'} className="card-timing-autofit-alert">
                {t`Target applied.`} {t`Delta`}: {autoFitResult.deltaFrames} {t`frames`} ({formatSignedClock(autoFitResult.deltaFrames, fps)})
              </Alert>
            ) : null}

            <div className="card-timing-layout">
              <div className="card-timeline-list">
                {timeline.map((item: CardTimelineItem) => {
                  const widthPercent: number = (item.durationFrames / maxProjectFrames) * 100;
                  const fadeInPercent: number = (item.cardFadeInFrames / Math.max(1, item.durationFrames)) * 100;
                  const fullyUpPercent: number = (item.cardDurationFullyUpFrames / Math.max(1, item.durationFrames)) * 100;
                  const fadeOutPercent: number = (item.cardFadeOutFrames / Math.max(1, item.durationFrames)) * 100;
                  const slugPercent: number = Math.max(0, 100 - fadeInPercent - fullyUpPercent - fadeOutPercent);
                  const isSelected: boolean = item.cardGroupId === selectedCardGroupId;

                  return (
                    <button
                      key={item.cardGroupId}
                      className={`card-timeline-row${isSelected ? ' selected' : ''}`}
                      onClick={() => setSelectedCardGroupId(item.cardGroupId)}
                      type="button"
                    >
                      <div className="card-timeline-row-head">
                        <span className="card-name">{item.cardDisplayName}</span>
                        <span className="card-range">
                          {formatFramesToClock(item.startFrame, fps)} - {formatFramesToClock(item.endFrame, fps)}
                        </span>
                      </div>
                      <div className="card-timeline-bar-shell" style={{ width: `${Math.max(8, widthPercent)}%` }}>
                        <div className="card-timeline-segment fade-in" style={{ width: `${fadeInPercent}%` }} />
                        <div className="card-timeline-segment fully-up" style={{ width: `${fullyUpPercent}%` }} />
                        <div className="card-timeline-segment fade-out" style={{ width: `${fadeOutPercent}%` }} />
                        <div className="card-timeline-segment slug" style={{ width: `${slugPercent}%` }} />
                      </div>
                      <div className="card-timeline-row-foot">
                        <span>{formatFramesToClock(item.durationFrames, fps)}</span>
                        <span>{item.durationFrames} {t`frames`}</span>
                        {item.isMixedTiming ? <span className="card-mixed-chip">{t`Mixed timing normalized on edit`}</span> : null}
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="card-timing-side">
                <div className="card-timing-panel">
                  <h2>{t`Card Settings`}</h2>
                  {selectedCard ? (
                    <>
                      <div className="card-selected-name">{selectedCard.cardDisplayName}</div>
                      <div className="card-field-grid">
                        {timingFields.map(({ field, label }: TimingFieldConfig) => (
                          <RowInput
                            key={field}
                            id={`card-${field}`}
                            label={label}
                            value={selectedCard[field]}
                            unit={String(t`frames`)}
                            onChange={(value: string) => handleManualTimingChange(field, value)}
                          />
                        ))}
                      </div>
                      <div className="card-full-time">
                        {t`Full time`}: {formatFramesToClock(selectedCard.durationFrames, fps)} ({selectedCard.durationFrames} {t`frames`})
                      </div>
                    </>
                  ) : (
                    <p>{t`No cards available.`}</p>
                  )}
                </div>

                <div className="card-timing-panel">
                  <h2>{t`Set Target Time`}</h2>
                  <p>{t`Set project target and auto-balance fully-up duration across all cards.`}</p>
                  <Button
                    variant="contained"
                    onClick={openTargetDialog}
                    disabled={timeline.length === 0}
                  >
                    {t`Set Target Time`}
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <Dialog open={targetDialogOpen} onClose={() => setTargetDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t`Set Target Time`}</DialogTitle>
        <DialogContent className="card-timing-target-dialog">
          <RowInput
            id="target-time-input"
            label={String(t`Target time`)}
            value={targetTimeInput}
            unit="mm:ss:ff"
            type="text"
            placeholder="00:00:00"
            onChange={setTargetTimeInput}
          />
          <RowInput
            id="target-fade-in-input"
            label={String(t`Fade In`)}
            value={targetFadeInInput}
            unit={String(t`frames`)}
            onChange={setTargetFadeInInput}
          />
          <RowInput
            id="target-fade-out-input"
            label={String(t`Fade Out`)}
            value={targetFadeOutInput}
            unit={String(t`frames`)}
            onChange={setTargetFadeOutInput}
          />
          <RowInput
            id="target-slug-input"
            label={String(t`Slug`)}
            value={targetSlugInput}
            unit={String(t`frames`)}
            onChange={setTargetSlugInput}
          />
          {targetInputError ? <Alert severity="error">{targetInputError}</Alert> : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTargetDialogOpen(false)} disabled={isApplyingTarget}>{t`Cancel`}</Button>
          <Button
            onClick={handleApplyTargetTime}
            variant="contained"
            disabled={isApplyingTarget}
            startIcon={isApplyingTarget ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {isApplyingTarget ? t`Applying...` : t`Apply`}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CardTiming;
